/**
 * Shared macro status logic — single source of truth for the entire app.
 *
 * Status tiers:
 *   on-track        = all macros within goal-specific On Track ranges
 *   slightly-off    = all macros within Slightly Off ranges (UI label only)
 *   needs-adjustment = any macro outside Slightly Off ranges
 *
 * Goal-specific tolerance ranges mirror the backend exactly.
 */

var GOAL_TOLERANCE_RANGES = {
  'Body Recomposition': {
    on_track:    { calories: [92, 108], protein: [90, 110], fat: [80, 120], carbs: [80, 115] },
    slightly_off: { calories: [87, 113], protein: [85, 115], fat: [75, 125], carbs: [75, 120] },
  },
  'Lose Body Fat': {
    on_track:    { calories: [88, 103], protein: [90, 110], fat: [80, 115], carbs: [80, 115] },
    slightly_off: { calories: [83, 108], protein: [85, 115], fat: [75, 120], carbs: [75, 120] },
  },
  'Lose Weight': {
    on_track:    { calories: [88, 103], protein: [90, 110], fat: [80, 115], carbs: [80, 115] },
    slightly_off: { calories: [83, 108], protein: [85, 115], fat: [75, 120], carbs: [75, 120] },
  },
  'Build Muscle': {
    on_track:    { calories: [95, 108], protein: [92, 110], fat: [80, 120], carbs: [85, 115] },
    slightly_off: { calories: [90, 113], protein: [87, 115], fat: [75, 125], carbs: [80, 120] },
  },
  'Gain Weight': {
    on_track:    { calories: [95, 115], protein: [85, 135], fat: [75, 130], carbs: [70, 120] },
    slightly_off: { calories: [90, 120], protein: [80, 140], fat: [70, 135], carbs: [65, 125] },
  },
  'Maintain': {
    on_track:    { calories: [90, 110], protein: [85, 115], fat: [80, 120], carbs: [80, 120] },
    slightly_off: { calories: [85, 115], protein: [80, 120], fat: [75, 125], carbs: [75, 125] },
  },
  'Improve Health': {
    on_track:    { calories: [90, 110], protein: [85, 115], fat: [80, 120], carbs: [80, 120] },
    slightly_off: { calories: [85, 115], protein: [80, 120], fat: [75, 125], carbs: [75, 125] },
  },
};

var DEFAULT_RANGES = GOAL_TOLERANCE_RANGES['Maintain'];

/**
 * Get tolerance ranges for a specific goal.
 * @param {string} goal
 * @returns {{ on_track: Object, slightly_off: Object }}
 */
export function getToleranceRanges(goal) {
  return GOAL_TOLERANCE_RANGES[goal] || DEFAULT_RANGES;
}

/**
 * Calculate the macro percentage for a single macro.
 * @param {number} achieved
 * @param {number} target
 * @returns {number} percentage (0-200+)
 */
function macroPct(achieved, target) {
  if (!target || target <= 0) return 100;
  return Math.round((achieved / target) * 100);
}

/**
 * Check if a macro value is within a range.
 * @param {number} pct
 * @param {number[]} range - [lo, hi]
 * @returns {boolean}
 */
function inRange(pct, range) {
  return pct >= range[0] && pct <= range[1];
}

/**
 * Determine the macro status for a day.
 *
 * @param {Object} totals   - { calories, protein, carbs, fat }
 * @param {Object} target   - { calories, protein, carbs, fat }
 * @param {string} [goal]   - User's fitness goal (default: 'Maintain')
 * @returns {'on-track' | 'slightly-off' | 'needs-adjustment'}
 */
export function getMacroStatus(totals, target, goal) {
  var ranges = getToleranceRanges(goal || 'Maintain');
  var macros = ['calories', 'protein', 'carbs', 'fat'];

  var allOnTrack = true;
  var allSlightlyOff = true;

  for (var i = 0; i < macros.length; i++) {
    var key = macros[i];
    var pct = macroPct(totals[key] || 0, target[key] || 0);
    if (!inRange(pct, ranges.on_track[key])) {
      allOnTrack = false;
    }
    if (!inRange(pct, ranges.slightly_off[key])) {
      allSlightlyOff = false;
    }
  }

  if (allOnTrack) return 'on-track';
  if (allSlightlyOff) return 'slightly-off';
  return 'needs-adjustment';
}

/**
 * Get display properties for a status.
 * @param {'on-track' | 'slightly-off' | 'needs-adjustment'} status
 * @returns {{ label: string, textColor: string, bgColor: string, borderColor: string }}
 */
export function getStatusDisplay(status) {
  if (status === 'on-track') {
    return {
      label: 'On Track',
      textColor: 'text-accent',
      bgColor: 'bg-accent/10',
      borderColor: 'border-accent/20',
    };
  }
  if (status === 'slightly-off') {
    return {
      label: 'Slightly Off',
      textColor: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/20',
    };
  }
  return {
    label: 'Needs Adjustment',
    textColor: 'text-red-500',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/20',
  };
}

/**
 * Get color class for a single macro bar based on its percentage vs goal ranges.
 * Returns neutral by default, only colored when clearly problematic.
 * @param {number} pct
 * @param {string} macro - 'calories' | 'protein' | 'carbs' | 'fat'
 * @param {string} [goal]
 * @returns {string} tailwind color class
 */
export function getMacroBarColor(pct, macro, goal) {
  var ranges = getToleranceRanges(goal || 'Maintain');
  if (inRange(pct, ranges.on_track[macro])) return 'bg-accent';
  if (inRange(pct, ranges.slightly_off[macro])) return 'bg-amber-500';
  return 'bg-red-500';
}

/**
 * Get text color for a single macro value.
 * Neutral by default — only colored when clearly off.
 * @param {number} pct
 * @param {string} macro
 * @param {string} [goal]
 * @returns {string}
 */
export function getMacroTextColor(pct, macro, goal) {
  var ranges = getToleranceRanges(goal || 'Maintain');
  if (inRange(pct, ranges.on_track[macro])) return '';
  if (inRange(pct, ranges.slightly_off[macro])) return 'text-amber-500';
  return 'text-red-500';
}

export { GOAL_TOLERANCE_RANGES };
